import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import { getErrorMessage } from "@/core/api/errors";
import type { useCreateAiScriptExplanationChat } from "@/payroll/ai-script/query";
import type { AiScriptChatSelectionContext } from "@/payroll/ai-script/types";
import {
  buildAssistantErrorMessage,
  buildMessageId,
  CHAT_VIEW,
  type ChatView,
} from "./chat-session-helpers";
import { formatSelectionContextSentence } from "./selection-context";
import type { ChatMessage } from "./types";
import { useComposerImages } from "./use-composer-images";
import type { ThreadLiveStates } from "./use-thread-live-states";

export const useChatSend = ({
  runId,
  chatPath,
  input,
  isBusy,
  selectedThreadId,
  setSelectedThreadId,
  attachedContext,
  sendMessage,
  refetchThreads,
  setMessages,
  setView,
  setIsStartingNewTurn,
  resetComposer,
  wasAtBottomRef,
  followedTurnKeysRef,
  loadedThreadIdRef,
  selectedThreadIdRef,
  liveStates: {
    setLocalRunningTurnsByThreadId,
    markThreadFollowing,
    appendLiveTextDelta,
    appendLiveProgressStep,
    finishThreadTurn,
    setPendingThreadResponse,
    clearLiveResponse,
  },
}: {
  runId: string;
  chatPath: string;
  input: string;
  isBusy: boolean;
  selectedThreadId: string | null;
  setSelectedThreadId: Dispatch<SetStateAction<string | null>>;
  attachedContext: AiScriptChatSelectionContext | null;
  sendMessage: ReturnType<typeof useCreateAiScriptExplanationChat>["mutate"];
  refetchThreads: () => Promise<unknown>;
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  setView: Dispatch<SetStateAction<ChatView>>;
  setIsStartingNewTurn: Dispatch<SetStateAction<boolean>>;
  resetComposer: () => void;
  wasAtBottomRef: MutableRefObject<boolean>;
  followedTurnKeysRef: MutableRefObject<Set<string>>;
  loadedThreadIdRef: MutableRefObject<string | null>;
  selectedThreadIdRef: MutableRefObject<string | null>;
  liveStates: ThreadLiveStates;
}) => {
  const { chatImages, clearImages, takeImages, imagePath } =
    useComposerImages(chatPath);
  const { images } = chatImages;
  // targetThreadId bypasses selected-thread state when starting a new chat in the same tick.
  const handleSend = (text?: string, targetThreadId?: string | null) => {
    const trimmedInput = (text ?? input).trim();
    if (!trimmedInput || isBusy) return;
    let activeThreadId =
      targetThreadId === undefined ? selectedThreadId : targetThreadId;

    const apiContent = attachedContext
      ? `${trimmedInput}\n\n${formatSelectionContextSentence(attachedContext)}`
      : trimmedInput;

    wasAtBottomRef.current = true;
    if (activeThreadId) {
      markThreadFollowing(activeThreadId, true);
    } else {
      setIsStartingNewTurn(true);
    }
    setMessages((currentMessages) => [
      ...currentMessages,
      {
        id: buildMessageId(),
        role: "user",
        content: trimmedInput,
        apiContent,
        selectionContext: attachedContext,
        localImages: images,
      },
    ]);
    resetComposer();
    const restoreImages = takeImages();
    // A message the API refused (a turn already running, an unreadable image)
    // was never saved: its screenshots go back to the composer.
    let accepted = false;

    sendMessage(
      {
        runId,
        message: apiContent,
        images,
        threadId:
          targetThreadId === undefined ? selectedThreadId : targetThreadId,
        onThread: (thread, turnId) => {
          accepted = true;
          activeThreadId = thread.id;
          setIsStartingNewTurn(false);
          // Mark followed up front so active_turn streaming does not open a second stream.
          followedTurnKeysRef.current.add(`${thread.id}:${turnId}`);
          loadedThreadIdRef.current = thread.id;
          setSelectedThreadId(thread.id);
          void refetchThreads();
          setLocalRunningTurnsByThreadId((currentTurns) => ({
            ...currentTurns,
            [thread.id]: turnId,
          }));
          markThreadFollowing(thread.id, true);
        },
        onTextDelta: (delta) => {
          const threadId = activeThreadId;
          if (!threadId) return;

          appendLiveTextDelta(threadId, delta);
        },
        onProgress: (label) => {
          const threadId = activeThreadId;
          if (!threadId) return;

          appendLiveProgressStep(threadId, label);
        },
      },
      {
        onSuccess: ({ response, thread, turnId }) => {
          setIsStartingNewTurn(false);
          followedTurnKeysRef.current.add(`${thread.id}:${turnId}`);
          finishThreadTurn(thread.id);
          setPendingThreadResponse(thread.id, response);
        },
        onError: (error: unknown) => {
          const message = getErrorMessage(error);
          const threadId = activeThreadId;
          setIsStartingNewTurn(false);
          if (!accepted) restoreImages();
          if (threadId) {
            finishThreadTurn(threadId);
            clearLiveResponse(threadId);
          }
          if (threadId && selectedThreadIdRef.current !== threadId) return;

          setMessages((currentMessages) => [
            ...currentMessages,
            buildAssistantErrorMessage(message),
          ]);
        },
      },
    );
  };

  const handleStartNewChatWithMessage = (text?: string) => {
    const trimmedInput = (text ?? input).trim();
    if (!trimmedInput || isBusy) return;

    setSelectedThreadId(null);
    loadedThreadIdRef.current = null;
    setMessages([]);
    setView(CHAT_VIEW.chat);
    handleSend(trimmedInput, null);
  };

  return {
    handleSend,
    handleStartNewChatWithMessage,
    chatImages,
    // Another conversation starts without the last one's screenshots.
    clearImages,
    imagePath,
  };
};
